import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { selectTerritories } from '../../../../../state/location/location.reducer';
import { Territory } from '../../../../classes/territory';
import { LocationService } from '../../../../../services/location.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GenStateAccess } from '../../../../../generated/serverModels/GenStateAccess';


@Component({
  selector: 'tgrid-state-request-form-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    AsyncPipe,
    ReactiveFormsModule,
  ],
  templateUrl: './state-request-form-modal.component.html',
  styleUrl: './state-request-form-modal.component.scss',
})
export class StateRequestFormModalComponent {
  private locationService = inject(LocationService);
  private popup = inject(MatSnackBar);
  private ref = inject(MatDialogRef<StateRequestFormModalComponent>);
  private territories$ = inject(Store).select(selectTerritories);
  protected data = inject<{ message?: string }>(MAT_DIALOG_DATA);

  protected filterControl = new FormControl<string>('');
  protected justificationControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  private selectedStates$ = new BehaviorSubject<Territory[]>([]);
  protected selectedStates: Observable<Territory[]> =
    this.selectedStates$.asObservable();

  protected filteredTerritories$: Observable<Territory[]> = combineLatest([
    this.filterControl.valueChanges.pipe(startWith('')),
    this.territories$,
    this.selectedStates$,
  ]).pipe(
    map(([filterValue, territories, selected]) => {
      const filter = (
        typeof filterValue === 'string' ? filterValue : ''
      ).toLowerCase();
      return territories.filter(
        (territory) =>
          !selected.some((state) => state.id === territory.id) &&
          territory.name.toLowerCase().includes(filter),
      );
    }),
    takeUntilDestroyed(),
  );

  protected addState(event: MatAutocompleteSelectedEvent): void {
    const territory: Territory = event.option.value;
    this.selectedStates$.next([...this.selectedStates$.getValue(), territory]);
    this.filterControl.setValue('', { emitEvent: true });
  }

  protected removeState(territory: Territory): void {
    this.selectedStates$.next(
      this.selectedStates$
        .getValue()
        .filter((state) => state.id !== territory.id),
    );
  }

  protected cancel(): void {
    this.ref.close();
  }

  protected submit(): void {
    this.justificationControl.markAsTouched();
    if (
      this.justificationControl.invalid ||
      this.selectedStates$.getValue().length === 0
    ) {
      return;
    }

    const payload: GenStateAccess = {
      stateAbbreviation: this.selectedStates$
        .getValue()
        .map((state) => state.abbreviation),
      reason: this.justificationControl.getRawValue().trim(),
    };

    this.locationService.submitStateAccessRequest(payload).subscribe({
      next: () => {
        this.popup.open(
          'State access request submitted successfully',
          'Close',
          {
            duration: 3000,
          },
        );
        this.ref.close(payload);
      },
      error: (err) => {
        console.error('Failed to submit state access request', err);
        this.popup.open(
          'Failed to submit state access request. Please try again later.',
          'Close',
          {
            duration: 3000,
          },
        );
      },
    });
  }

  protected displayFn(territory: Territory): string {
    return territory?.name ?? '';
  }
}
